import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import React, { useEffect, useState } from 'react'
import {
  ExportAllAdvertisersArg,
  GetAdvertiserReportsAllAdvertisersApiArg,
  useExportAllAdvertisers,
} from '../../../../app/api'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import {
  selectAdvertiserReportFilters,
  selectAdvertiserReportPagination,
  selectIsExpandAll,
  selectProject,
  setAdvertiserReportFilter,
  toggleExpand,
} from '../../model/paidResultsSlice'
import { AdvertiserReportFilterInterface } from '../../model/types/AdvertiserReportFilter.interface'
import { useTranslation } from 'react-i18next'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconMinimize } from '../../../../shared/components/icons/IconMinimize'
import { IconMaximize } from '../../../../shared/components/icons/IconMaximize'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import { Switch } from 'antd'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import clsx from 'clsx'
import styles from './PaidResultsPanel.module.scss'

export const PaidResultsPanel = () => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const currentProject = useAppSelector(selectProject)
  const advertiserFilter = useAppSelector<AdvertiserReportFilterInterface>(
    selectAdvertiserReportFilters,
  )
  const {
    project,
    dateRange: date,
    country,
    policy,
    advertiser,
    keyword,
    affiliate,
    show_hidden,
    engine,
    browser,
    is_decloak,
    show_shopping_ads,
    advertiserName,
    tags,
    reversed_filters,
  } = advertiserFilter
  const advertiserPagination = useAppSelector<IPagination>(selectAdvertiserReportPagination)
  const isExpandAll = useAppSelector(selectIsExpandAll)
  const { current, pageSize } = advertiserPagination
  const [queryParams, setQueryParams] = useState<Partial<ExportAllAdvertisersArg>>({
    page: current,
    'per-page': pageSize,
  })
  const dispatch = useAppDispatch()

  const [exportFile, { isFetching: exportFileLoading }] = useExportAllAdvertisers()

  useEffect(() => {
    if (!project) return

    setQueryParams({
      ...queryParams,
      projectId: +project,
      countryId: country || undefined,
      engineId: engine || undefined,
      browserId: browser || undefined,
      policy: policy as GetAdvertiserReportsAllAdvertisersApiArg['policy'],
      date,
      site: advertiser,
      keywordId: keyword || undefined,
      affiliateId: affiliate || undefined,
      showHidden: show_hidden,
      showShoppingAds: show_shopping_ads,
      isDecloak: is_decloak,
      advertiserId: advertiserName || undefined,
      tagIds: tags || undefined,
      reversedFilters: reversed_filters || undefined,
    })
  }, [advertiserFilter, advertiserPagination])

  useEffect(() => {
    if (!currentProject.has_shopping_ads) {
      dispatch(setAdvertiserReportFilter({ ...advertiserFilter, show_shopping_ads: false }))
    }
  }, [currentProject])

  return (
    <div className={clsx(styles.root)}>
      {
        {
          bluepear: (
            <AiButton
              unstyled
              onClick={() =>
                handleSendMessage({
                  message:
                    'Please provide a clear guide on how to use the Paid Results Report and explain its value.',
                })
              }
            >
              <span className={clsx(styles.aiButtonText)}>How the report works</span>
            </AiButton>
          ),
          robuscan: null,
          default: null,
        }[process.env.REACT_APP_PROJECT ?? 'default']
      }
      {currentProject.has_shopping_ads && (
        <span className={clsx(styles.showPlaRow)}>
          <Switch
            defaultChecked={show_shopping_ads}
            onClick={(e) => {
              dispatch(setAdvertiserReportFilter({ ...advertiserFilter, show_shopping_ads: e }))
            }}
          ></Switch>
          {t('buttons.showPLAs')}
          <BpTooltip title={t('buttons.other.showPLAsTooltip')} />
        </span>
      )}

      <span className={clsx(styles.buttonsRow)}>
        <BpButton
          iconLeft={isExpandAll ? <IconMinimize /> : <IconMaximize />}
          size='sm'
          theme='outline'
          type='button'
          onClick={() => dispatch(toggleExpand())}
        ></BpButton>
        <ExportDrop
          columns={[
            {
              label: t('pages.paidResults.advertiserReport'),
              bold: true,
              child: [
                { keys: ['advertiser'], label: t('table.columns.domain') },
                { keys: ['advertisers'], label: t('table.columns.advertiser') },
                { keys: ['tags'], label: t('table.columns.tags') },
                { keys: ['count_total'], label: t('table.columns.adsAnalyzed') },
                { keys: ['keywords_count'], label: t('table.columns.keywords') },
                { keys: ['visibility'], label: t('table.columns.visibility') },
                { keys: ['affiliates_count'], label: t('table.columns.affiliates') },
                { keys: ['progress_status'], label: t('table.columns.progressStatus') },
                { keys: ['last_found'], label: t('table.columns.lastFound') },
              ],
            },
            {
              keys: ['latest_examples'],
              label: t('buttons.latestExamples'),
              tooltip: t('buttons.other.latestExamplesTooltip'),
              bold: true,
            },
          ]}
          loading={exportFileLoading}
          onExport={(exportParams) => exportFile({ ...queryParams, ...exportParams })}
        />
      </span>
    </div>
  )
}
